"use client";

import { useEffect } from "react";
import { RefreshCwIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SpaceError } from "@/components/illustrations/space-error";

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
  }, [error]);

  const isOffline =
    error.name === "BackendOfflineError" ||
    error.message?.toLowerCase().includes("unavailable") ||
    error.message?.toLowerCase().includes("fetch failed");

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-8 text-center supports-[height:100dvh]:min-h-dvh">
      <SpaceError className="w-72 max-w-full" />
      <div className="flex flex-col gap-2">
        {isOffline ? (
          <>
            <h2 className="text-xl font-semibold text-foreground">System Offline</h2>
            <p className="max-w-sm text-sm text-muted-foreground">
              Akili is temporarily unavailable. We&apos;re working to restore
              service. Please try again in a moment.
            </p>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-foreground">
              Something went wrong
            </h2>
            <p className="max-w-sm text-sm text-muted-foreground">
              An unexpected error occurred. If the problem persists, please contact
              support.
            </p>
          </>
        )}
        {error.digest && (
          <p className="font-mono text-xs text-muted-foreground/60">
            {error.digest}
          </p>
        )}
      </div>
      <Button variant="outline" onClick={reset} className="gap-2">
        <RefreshCwIcon className="size-4" />
        Try again
      </Button>
    </div>
  );
}
